import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import styles from './Row.module.sass';
import { IEventTicketListItem } from '@types';
import Icon from 'components/Icon';
import Checkbox from 'components/Checkbox';
import { TicketRequestStatus } from '@core';
import { paths } from 'router/paths';

interface RowProps {
  item: IEventTicketListItem;
  onDecline: (id: string) => void;
  onApprove: (id: IEventTicketListItem) => void;
  onTogglePaid: (id: string, amount?: number) => void;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
}

const Row = ({ item, onDecline, onApprove, onTogglePaid, isSelected, onSelect }: RowProps) => {
  const navigate = useNavigate();
  
  const approveTicket = () => {
    onApprove(item);
  };

  const handleDeclineTicket = (id: string) => {
    const confirmed = window.confirm('ნამდვილად გსურთ უარი?');
    if (!confirmed) {
      return;
    }
    onDecline(id);
  };

  const navigateToDetails = (id: string) => {
    navigate(paths.USER_DETAILS_PATH.replace(':id', id));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(e.target.checked);
  };

  const isApproved = item.status === TicketRequestStatus.Approved || item.status === TicketRequestStatus.Delivered;

  const handleTogglePaid = () => {
    if (item.paid) {
      const confirmed = window.confirm('ნამდვილად გსურთ გადახდის სტატუსის მოხსნა?');
      if (!confirmed) {
        return;
      }
      onTogglePaid(item.id);
      return;
    }

    const input = window.prompt('შეიყვანეთ გადახდილი თანხა (₾)');
    if (input === null) {
      return;
    }

    const amount = Number(input.replace(',', '.').trim());
    if (!Number.isFinite(amount) || amount < 0) {
      window.alert('შეიყვანეთ სწორი თანხა');
      return;
    }

    onTogglePaid(item.id, amount);
  };

  return (
    <>
      <div className={cn(styles.row)}>
        <div className={styles.col}>
          <Checkbox
            className=""
            value={isSelected}
            onChange={handleCheckboxChange}
            classCheckboxTick=""
            content=""
            reverse={false}
          />
        </div>
        <div className={styles.col}>
          <div
            className={styles.item}
            onClick={() => {
              navigateToDetails(item.user.id);
            }}
          >
            <div className={styles.avatar}>
              <img
                src={item.user.profilePhoto !== '' ? item.user.profilePhoto : '/images/content/avatar-2.jpg'}
                alt="Profile Photo"
              />
            </div>
            <div className={styles.details}>
              <div className={styles.user}>{`${item.user.firstName} ${item.user.lastName}`}</div>
              <div className={styles.city}>{item.user.phoneNumber}</div>
            </div>
          </div>
        </div>

        <div className={styles.col}>
          <div>
            {item.quantity}
            {item.additionalUsers && item.additionalUsers.length > 0 && (
              <span>
                (
                {item.additionalUsers.map((name, index) => (
                  <span key={index}>, {name}</span>
                ))}
                )
              </span>
            )}
          </div>
        </div>

        <div className={styles.col}>
          <div>{item.updatedAt}</div>
        </div>

        <div className={styles.col}>
          {item.status === TicketRequestStatus.Approved && (
            <div className={cn('status-green', styles.status)}>დადასტურებულია</div>
          )}

          {item.status === TicketRequestStatus.Pending && (
            <div className={cn('status-yellow', styles.status)}>მოთხოვნილი</div>
          )}

          {item.status === TicketRequestStatus.Declined && (
            <div className={cn('status-red', styles.status)}>უარყოფილია</div>
          )}

          {item.status === TicketRequestStatus.Delivered && (
            <div className={cn('status-blue', styles.status)}>გაცემული</div>
          )}
        </div>

        <div className={styles.col}>
          {item.checkinDate && (
            <div className={cn('button', 'button-small', 'button-stroke-blue', styles.button)}>კი</div>
          )}
          {!item.checkinDate && (
            <button className={cn('button', 'button-small', 'button-stroke-red', styles.button)}>არა</button>
          )}
        </div>

        <div className={styles.col}>
          {isApproved && (
            <button
              onClick={handleTogglePaid}
              className={cn(
                'button',
                'button-small',
                item.paid ? 'button-stroke-green' : 'button-stroke-red',
                styles.button,
              )}
            >
              {item.paid ? (item.paidAmount != null ? `კი · ${item.paidAmount} ₾` : 'კი') : 'არა'}
            </button>
          )}
        </div>

        <div className={styles.col}>
          {(item.status == TicketRequestStatus.Declined || item.status == TicketRequestStatus.Pending) && (
            <button
              onClick={approveTicket}
              className={cn('button', 'button-small', 'button-stroke-green', styles.button)}
            >
              <Icon name="check" size="24" />
            </button>
          )}

          {(item.status == TicketRequestStatus.Pending || item.status == TicketRequestStatus.Approved) && (
            <button
              onClick={() => void handleDeclineTicket(item.id)}
              className={cn('button', 'button-small', 'button-stroke-red', styles.button)}
            >
              <Icon name="close" size="24" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Row;
